// useHoverMenu — small hover/touch-aware dropdown controller.
//
// Returns `{ open, triggerProps, menuProps, close }`. Spread `triggerProps`
// onto the wrapping pill/chip and `menuProps` onto the dropdown container.
//
// Behaviour:
//   - Hover devices: mouseenter after `openDelay` (~120ms) opens the menu
//     in HOVER-TRANSIENT state — it closes on mouseleave after `closeDelay`.
//     CLICKING the trigger while open promotes it to LOCKED state — the
//     menu stays open after mouseleave and only closes on (a) another
//     click on the trigger, (b) a click outside the wrapper, or (c) an
//     item handler calling `close()`. Re-entering the trigger or menu
//     cancels a pending close. Keyboard focus inside also keeps it open.
//   - Touch / coarse-pointer devices: tap the trigger to toggle (always
//     locked when open). A document-level `pointerdown` listener closes
//     it on outside taps.
//
// No window-level mousemove listeners; no portals; pure inline state.
//
// Hover is detected once on mount via `(hover: hover)` media query. We
// also subscribe to changes (a hybrid laptop with detachable keyboard
// can flip between modes) but it's an edge case — the result lives in
// state so re-renders pick it up.

import { useCallback, useEffect, useRef, useState } from "react";
import type { FocusEvent } from "react";

export interface UseHoverMenuOptions {
  /** ms before opening on hover. Defaults to 120. */
  openDelay?: number;
  /** ms before closing after the cursor leaves trigger+menu. Defaults to 250. */
  closeDelay?: number;
  /**
   * Additional element ref to treat as "inside" for outside-click dismissal.
   * Useful when the menu is rendered via createPortal outside the wrapRef tree.
   */
  excludeRef?: React.RefObject<HTMLElement | null>;
}

export interface HoverMenuTriggerProps {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onFocus: (e: FocusEvent<HTMLElement>) => void;
  onBlur: (e: FocusEvent<HTMLElement>) => void;
  onClick: () => void;
}

export interface HoverMenuMenuProps {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onFocus: (e: FocusEvent<HTMLElement>) => void;
  onBlur: (e: FocusEvent<HTMLElement>) => void;
}

export interface UseHoverMenuResult {
  open: boolean;
  /** Imperatively close. Useful when an item handler navigates away. */
  close: () => void;
  /** Spread onto the trigger pill/chip wrapper. */
  triggerProps: HoverMenuTriggerProps;
  /** Spread onto the dropdown menu container. */
  menuProps: HoverMenuMenuProps;
  /** Refs the wrapper element; used for outside-tap dismissal on touch. */
  wrapRef: (el: HTMLElement | null) => void;
}

function detectHover(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return true;
  try {
    return window.matchMedia("(hover: hover)").matches;
  } catch {
    return true;
  }
}

export function useHoverMenu(
  options: UseHoverMenuOptions = {},
): UseHoverMenuResult {
  // Forgiving close delay so users don't trigger close by briefly
  // wandering near the edge of the menu — paired with the trigger/menu
  // touching directly (no positional gap), this gives a wide hover-safe zone.
  const { openDelay = 120, closeDelay = 350, excludeRef } = options;

  const [open, setOpen] = useState(false);
  // Locked = "click-committed". When locked, mouseleave does NOT close;
  // only an explicit close path does (outside click / re-click trigger /
  // item handler calling close()). Hover-transient menus have open=true
  // and locked=false. Touch menus always lock when opened.
  const [locked, setLocked] = useState(false);
  const [isHover, setIsHover] = useState<boolean>(detectHover);

  // Timers — separate for open/close so we can cancel either independently.
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapEl = useRef<HTMLElement | null>(null);

  // Track whether keyboard focus is currently inside (keeps the menu open
  // even after a brief mouseleave as a sighted-keyboard user tabs through).
  const focusInside = useRef(false);

  const clearOpenTimer = () => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
  };
  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  // Subscribe to hover-capability changes (detachable keyboards etc.).
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    let mql: MediaQueryList;
    try {
      mql = window.matchMedia("(hover: hover)");
    } catch {
      return;
    }
    const onChange = () => setIsHover(mql.matches);
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else if (mql.addListener) mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else if (mql.removeListener) mql.removeListener(onChange);
    };
  }, []);

  // Outside-tap closes whenever the menu is LOCKED. Touch-mode menus are
  // always locked when open, so this also covers the original touch case.
  // Hover-transient menus (open && !locked) don't get this listener —
  // they close on mouseleave naturally.
  useEffect(() => {
    if (!open || !locked) return;
    function onDocPointer(e: PointerEvent) {
      const el = wrapEl.current;
      if (!el) return;
      if (e.target instanceof Node && el.contains(e.target)) return;
      const excl = excludeRef?.current;
      if (excl && e.target instanceof Node && excl.contains(e.target)) return;
      setLocked(false);
      setOpen(false);
    }
    document.addEventListener("pointerdown", onDocPointer);
    return () => document.removeEventListener("pointerdown", onDocPointer);
  }, [open, locked, excludeRef]);

  // Cleanup any pending timers on unmount.
  useEffect(() => {
    return () => {
      clearOpenTimer();
      clearCloseTimer();
    };
  }, []);

  const scheduleOpen = useCallback(() => {
    clearCloseTimer();
    if (open) return;
    clearOpenTimer();
    openTimer.current = setTimeout(() => {
      setOpen(true);
      openTimer.current = null;
    }, openDelay);
  }, [open, openDelay]);

  const scheduleClose = useCallback(() => {
    clearOpenTimer();
    if (focusInside.current) return; // keyboard user navigating
    if (locked) return; // click-locked menus ignore hover-out
    clearCloseTimer();
    closeTimer.current = setTimeout(() => {
      setOpen(false);
      closeTimer.current = null;
    }, closeDelay);
  }, [closeDelay, locked]);

  const close = useCallback(() => {
    clearOpenTimer();
    clearCloseTimer();
    setLocked(false);
    setOpen(false);
  }, []);

  const onTriggerMouseEnter = useCallback(() => {
    if (!isHover) return;
    scheduleOpen();
  }, [isHover, scheduleOpen]);

  const onTriggerMouseLeave = useCallback(() => {
    if (!isHover) return;
    scheduleClose();
  }, [isHover, scheduleClose]);

  const onMenuMouseEnter = useCallback(() => {
    if (!isHover) return;
    clearCloseTimer();
  }, [isHover]);

  const onMenuMouseLeave = useCallback(() => {
    if (!isHover) return;
    scheduleClose();
  }, [isHover, scheduleClose]);

  // Focus tracking — keep open while focus is inside, close shortly after
  // it leaves. Use relatedTarget to detect focus moving within the wrapper.
  const onFocusIn = useCallback((e: FocusEvent<HTMLElement>) => {
    focusInside.current = true;
    clearCloseTimer();
    if (!open) {
      // Open without delay for keyboard users — they've explicitly tabbed in.
      clearOpenTimer();
      setOpen(true);
    }
    void e;
  }, [open]);

  const onFocusOut = useCallback((e: FocusEvent<HTMLElement>) => {
    const next = e.relatedTarget as Node | null;
    const el = wrapEl.current;
    if (el && next && el.contains(next)) {
      // Focus moved within the same wrapper — still inside.
      return;
    }
    focusInside.current = false;
    if (isHover) {
      scheduleClose();
    } else {
      // Touch fallback: close on focus leaving.
      setOpen(false);
    }
  }, [isHover, scheduleClose]);

  const onTriggerClick = useCallback(() => {
    clearOpenTimer();
    clearCloseTimer();
    if (isHover) {
      // Hover devices, three cases:
      //   1. Already LOCKED → toggle-off (click on locked = close).
      //   2. Open but not locked (hover-transient) → upgrade to LOCKED.
      //      Menu stays open and survives mouseleave.
      //   3. Closed → open immediately AND lock (e.g. user clicked before
      //      the hover-open delay elapsed).
      if (locked) {
        setLocked(false);
        setOpen(false);
      } else if (open) {
        setLocked(true);
      } else {
        setOpen(true);
        setLocked(true);
      }
      return;
    }
    // Touch devices: tap toggles. When opening, always lock so the outside-
    // tap listener (which gates on `locked`) covers the touch case.
    if (open) {
      setOpen(false);
      setLocked(false);
    } else {
      setOpen(true);
      setLocked(true);
    }
  }, [isHover, open, locked]);

  const wrapRef = useCallback((el: HTMLElement | null) => {
    wrapEl.current = el;
  }, []);

  return {
    open,
    close,
    wrapRef,
    triggerProps: {
      onMouseEnter: onTriggerMouseEnter,
      onMouseLeave: onTriggerMouseLeave,
      onFocus: onFocusIn,
      onBlur: onFocusOut,
      onClick: onTriggerClick,
    },
    menuProps: {
      onMouseEnter: onMenuMouseEnter,
      onMouseLeave: onMenuMouseLeave,
      onFocus: onFocusIn,
      onBlur: onFocusOut,
    },
  };
}
